'use client';
import Loading from '@/app/(withLayout)/loading';
import { ConfirmationDetails } from '@/components/confirmationDetails';
import { sendOnbEvent } from '@/components/newRelic';
import { setAdditionalData } from '@/redux/additionalDataSlice';
import { pushErrorReason } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Grid, Stack, Typography } from '@mui/material';
import { installmentRates, reviewAndSubmit } from '@repo/onb-api';
import { COLORS, MaterialIcon, OrderCard } from '@repo/ui';
import { formatter, setClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function ReviewAndSubmit({
  stepname: stepName,
  stepdata: stepData,
}: {
  stepname: reviewAndSubmit['name'];
  stepdata?: reviewAndSubmit['variables'];
}): React.JSX.Element {
  const dispatch = useDispatch();
  const router = useRouter();
  const errorState = useSelector((state: RootState) => state.errorState);
  const { accessKey } = useSelector((state: RootState) => state.flowdata);
  const isFundsInsufficient = errorState?.error === 'INSUFFICIENT_FUNDS';
  const {
    requestedAmount = 0,
    installmentCount = 0,
    installmentRates: { TEA: tea = 0, CFT: cft = 0, TNA: tna = 0 } = {},
    installmentAmount = 0,
    installmentFirstDueDate = '2022-01-01',
    paymentMethod,
    bankAccountReference,
    cardNumber = '1234********1234',
    cardIssuer,
    termsAndConditions,
    paymentSchedule,
    documentNumber,
    gender,
    fullName,
    birthDate,
    taxpayerId,
    email,
    mobilePhoneNumber,
    province,
    withInterest,
    installmentPlanType,
  } = stepData || {};

  const installmentsOverride = {
    ...stepData?.installmentRates,
    CFTTEA: stepData?.installmentRates?.CFT,
  } as installmentRates;

  const isCuotas = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas';
  const additionalData = useSelector((state: RootState) => state.additionalData);
  const { merchantName = '', price = '', merchantOrderId = '', policyProduct } = additionalData;
  const subtitle = isCuotas
    ? policyProduct === 'EFECTIVOC'
      ? 'Revisá tus datos antes de confirmar la operación'
      : 'Revisá tus datos antes de confirmar la operación y disfrutar tu compra.'
    : 'Revisá tus datos antes de confirmar tu préstamo y empezar a disfrutarlo.';

  useEffect(() => {
    if (isFundsInsufficient) {
      sendOnbEvent({
        type: 'error',
        subtype: 'insufficientFunds',
        severity: 'medium',
      });
    }
  }, [isFundsInsufficient]);

  useEffect(() => {
    if (!accessKey) {
      dispatch(pushErrorReason('accessKey'));
      router.push('/error');
    }
    setClientCookie('requestedAmount', `${requestedAmount}`);
    dispatch(setAdditionalData({ requestedAmount: `${requestedAmount}` }));
  }, [requestedAmount]);

  return !accessKey ? (
    <Loading />
  ) : (
    <StepWrapper>
      <Stack gap={1}>
        <Typography variant="h1">Estamos casi listos</Typography>
        <Typography variant="h2">{subtitle}</Typography>
      </Stack>
      {isCuotas ? (
        <>
          <OrderCard
            merchantName={merchantName || ''}
            merchantOrderId={merchantOrderId || ''}
            price={price || ''}
            isLoan={policyProduct === 'EFECTIVOC'}
          />
          <Grid
            container
            direction={'column'}
            sx={{
              width: '100%',
              p: 2,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'primary.main',
            }}
          >
            <Grid container direction={'column'} gap={1}>
              <Grid container direction={'row'} gap={1} alignItems="center" justifyContent={'space-between'}>
                <Typography variant="h2" fontSize={18} fontWeight={'500'}>
                  {policyProduct === 'EFECTIVOC' ? 'Lo pagás en' : 'Tu compra en cuotas'}
                </Typography>
              </Grid>
              <Grid container direction={'row'} gap={1} alignItems="center">
                <Typography
                  component={'span'}
                  variant="h1"
                  color="primary.main"
                >{`${installmentCount} cuotas`}</Typography>
                {!withInterest && (
                  <Typography variant="body2" color="primary.main">
                    Sin Interés
                  </Typography>
                )}
              </Grid>
              <Grid container direction={'row'} gap={1} alignItems="center">
                <Typography
                  component={'span'}
                  variant="h1"
                  color="primary.main"
                >{`${formatter(installmentAmount.toString(), 'currency')}`}</Typography>
                <Typography variant="body2" color="primary.main">
                  por mes
                </Typography>
              </Grid>
              {/* <Typography variant="subtitle2">Total a pagar: {formatter(finalAmount, 'currency')}</Typography> */}
            </Grid>
          </Grid>
          {installmentPlanType === 'UPFRONT_PAYMENT' ? (
            <>
              <Grid
                container
                sx={{ gap: 3 }}
                direction={'row'}
                wrap="nowrap"
                alignItems={'center'}
                justifyContent={'center'}
              >
                <MaterialIcon color="primary" containerType="circle" icon="calendar_month" />
                <Typography variant="body2" color={'secondary.main'}>
                  Pagás la primera cuota hoy
                </Typography>
              </Grid>
              <Grid
                container
                sx={{ gap: 3 }}
                direction={'row'}
                wrap="nowrap"
                alignItems={'center'}
                justifyContent={'center'}
              >
                <MaterialIcon color="primary" containerType="circle" icon="credit_card" />
                <Typography variant="body2" color={'secondary.main'}>
                  Pagás con tarjeta de débito
                </Typography>
              </Grid>
            </>
          ) : (
            <Grid container sx={{ gap: 3 }} direction={'row'} wrap="nowrap" alignItems={'center'}>
              <MaterialIcon color="primary" containerType="circle" icon="calendar_month" />
              <Typography variant="body2" color={'secondary.main'}>
                Pagás la primera cuota el {formatter(installmentFirstDueDate, 'date')}
              </Typography>
            </Grid>
          )}
        </>
      ) : (
        <Stack
          direction="column"
          p={2}
          sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 2, gap: 2 }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body3" width={130}>
              Monto que estás solicitando
            </Typography>
            <Typography
              component={'span'}
              variant="h1"
              color="primary.main"
            >{`${formatter(requestedAmount.toString(), 'currency')}.-`}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body3" width={130}>
              Pagás la primera cuota el {formatter(installmentFirstDueDate, 'date')}
            </Typography>
            <Typography component={'span'} variant="h1" color="primary.main">
              {(installmentCount || 0) > 1 ? `${installmentCount} cuotas` : `${installmentCount} cuota`}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="primary.main" width={130}>
              Valor de la cuota por mes
            </Typography>
            <Typography component={'span'} variant="h1" fontSize={'2rem'} color="primary.main">
              {`${formatter(installmentAmount.toString(), 'currency')}.-`}
            </Typography>
          </Stack>
        </Stack>
      )}
      {bankAccountReference ? (
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: COLORS.background[0],
            borderRadius: 2,
            gap: 2,
            p: 2,
          }}
        >
          <Stack>
            <Typography variant="body3">Te lo depositamos en</Typography>
            <Typography variant="h2">{`CBU ${bankAccountReference}`}</Typography>
          </Stack>
        </Grid>
      ) : (
        <></>
      )}
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: COLORS.background[0],
          borderRadius: 2,
          border: isFundsInsufficient ? `1px solid ${COLORS.state.error}` : 'none',
          gap: 2,
          p: 2,
        }}
      >
        <Stack>
          <Typography variant="body3">{`${paymentMethod === 'DEBIT_CARD' ? 'Tus cuotas se debitarán de la tarjeta' : 'Tus cuotas se pagan'}`}</Typography>
          <Typography variant="h2">{`${paymentMethod === 'CASH' ? 'En locales habilitados o Pago Digital' : `${cardIssuer} ${cardNumber.slice(-8)}`}`}</Typography>
        </Stack>
      </Grid>
      {isFundsInsufficient ? (
        <Typography variant="body2" color={COLORS.state.error}>
          Algo no funcionó con la tarjeta seleccionada. Tocá el lápiz y elegí otra para continuar.
        </Typography>
      ) : (
        <></>
      )}
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: COLORS.background[0],
          borderRadius: 2,
          gap: 2,
          p: 2,
        }}
      >
        {/* @todo: replace requestedAmount with correct values */}
        <Stack>
          <Typography variant="body3" fontWeight={300}>
            TEA
          </Typography>
          <Typography variant="h2" fontWeight={300}>{`${formatter(tea, 'number')}%`}</Typography>
        </Stack>
        <Stack>
          <Typography variant="body3" fontWeight={300}>
            TNA
          </Typography>
          <Typography variant="h2" fontWeight={300}>{`${formatter(tna, 'number')}%`}</Typography>
        </Stack>
        <Stack>
          <Typography variant="body3" fontWeight="bold" fontSize={16}>
            CFTTEA
          </Typography>
          <Typography variant="h2" fontSize={18}>{`${formatter(cft, 'number')}%`}</Typography>
        </Stack>
      </Grid>
      <>
        {tna === 0 && (
          <Typography variant="body3">
            Préstamo con TNA = 0%: no se aplicará interés compensatorio. Sin embargo, si se registrara mora, el importe
            vencido e impago devengará un interés moratorio equivalente a una TNA=80 %, más un interés punitorio
            equivalente al cincuenta por ciento (50%) de dicha tasa moratoria.
          </Typography>
        )}
      </>
      <Typography variant="body3">
        Queremos que tengas toda la información clara antes de seguir. Al hacer “click” en el botón de “Confirmar” y
        tildar los casilleros de aceptación dispuestos a tal efecto, estás confirmando tu solicitud de préstamo a
        Crédito Directo S.A., CUIT 30-71210113-6, con domicilio en Tte. Gral. Juan Domingo Perón 646, piso 4° de la
        Ciudad Autónoma de Buenos Aires (“Directo”), los datos que proporcionaste en el proceso de solicitud y las
        condiciones particulares del préstamo que siguen a continuación (la “Solicitud”).
      </Typography>
      {/* <OnbForm<reviewAndSubmit> stepName={stepName!} submitLabel="Confirmar" submitBlacklist={['termsAndConditions']}>
        <Checkbox
          label="Acepto los términos y condiciones"
          {...connectedField({
            checkbox: true,
            stepName,
            name: 'hasTermsAccepted',
            required: true,
            beforeChange: (value) => !!value,
            errorHandler: {
              type: 'checkbox',
              errorText: 'Aceptá los términos y condiciones para continuar',
            },
          })}
        />
      </OnbForm> */}
      <ConfirmationDetails
        stepName={stepName}
        termsAndConditions={termsAndConditions}
        requestedAmount={requestedAmount}
        installmentCount={installmentCount}
        paymentSchedule={paymentSchedule}
        documentNumber={documentNumber}
        gender={gender}
        fullName={fullName}
        birthDate={birthDate}
        taxpayerId={taxpayerId}
        email={email}
        mobilePhoneNumber={mobilePhoneNumber}
        province={province}
        paymentMethod={paymentMethod}
        bankAccountReference={bankAccountReference}
        installmentRates={installmentsOverride}
        showSujetoObligado
      />
    </StepWrapper>
  );
}
